import { useEffect, useState } from "react";
import "./FilterBar.css";
import { Dropdown, MultiSelectDropdown, getDefaultQuarter } from "./FilterBar";
import { getTimePeriods, getTerritories } from "./api";

// Ported from core-compensation.component.ts's sortOrder (same constant used
// identically across CostBandTable.jsx/RangeBarsChart.jsx/
// DistributionChart.jsx/ComparisonChart.jsx) - fixed display order by
// territory_id (UK, Ireland, South Africa, Romania, India, Brazil).
const TERRITORY_SORT_ORDER = [4, 5, 2, 3, 1, 6];

/**
 * JobDetailTerritoryQuarterRow
 *
 * Job-detail-page counterpart to RoleListTerritoryQuarterRow.jsx - see that
 * file's docstring for the full background on why this is a separate,
 * conditionally-mounted component rather than shared state in FilterBar.
 *
 * Ported from non-core-compensation.component.ts's own Quarter/Territory
 * state (model.timePeriodId / model.territoryIds - unlimited, no
 * selectionLimit binding anywhere in the real template) and its own
 * loadData(), which fetches cost-band-detail data ONCE and hands it down as
 * a plain @Input to both the Cost Band Detail table and the Range Bars
 * chart - confirmed via compensation-range-chart.component.ts having no
 * HTTP call of its own at all, just `@Input() territoryData`. This
 * component IS that single fetch now - CostBandTable.jsx/RangeBarsChart.jsx/
 * DistributionChart.jsx no longer fetch independently (that was the actual
 * root cause of the stale-territory race, not a missing guard - old never
 * has more than one fetch in flight for this data to race against). They
 * just render whatever `data`/`loading` props App.js passes down from here.
 *
 * Old's loadData() also re-runs on the SAME instance (via
 * Config.stackModelChanged) when the Job Title changes while STAYING in
 * job-detail mode - so Quarter/Territory correctly persist across a
 * role-to-role switch there, never reset. App.js only conditionally renders
 * this component on activeJobTitleId truthy/falsy (the role-list <->
 * job-detail boundary), not on the specific job title id - so switching
 * roles within job-detail does not unmount this component either, matching
 * that exactly; only the fetch effect below re-runs (jobTitleId is one of
 * its dependencies), same as old's loadData() re-running on the same
 * instance.
 *
 * jobTitleId: from App.js's activeJobTitleId.
 * onDataChange({data, loading, error}): the one cost-band-detail fetch's
 * result - App.js hands `data`/`loading` straight through as props to
 * CostBandTable/RangeBarsChart/DistributionChart, and `data` alone to
 * FilterBar's Export button (matches old's rawModel/exportData emit - see
 * FilterBar.jsx's exportCompensationXls()).
 * onQuarterChange(quarter): needed by CostBandTable's own region-accordion
 * fetches (time_period_id) - display/secondary-fetch use only, no race risk
 * (those fire on explicit user clicks well after this settles).
 * onTerritoryIdsChange(ids): needed by ComparisonChart's client-side
 * territory filter (no fetch depends on it either).
 */
export default function JobDetailTerritoryQuarterRow({
  jobTitleId,
  onDataChange,
  onQuarterChange,
  onTerritoryIdsChange,
}) {
  const [quarters, setQuarters] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [quarter, setQuarter] = useState(null);
  const [territoryIds, setTerritoryIds] = useState([]);
  // FIXED 2026-08-05 (duplicate-rows bug): old's loadData() AWAITS its
  // Promise.all([timePeriod, territories, ...]) and resolves the default
  // quarter BEFORE building `param` and firing the one cost-band-detail
  // call - the quarter is never unresolved when that call goes out. This
  // component previously fired its data-fetch effect immediately on mount,
  // before getTimePeriods() resolved, sending no time_period_id -
  // cost_band_detail.sql treats a missing time_period_id as "match every
  // quarter" (unlike compensation_pivot.sql's COALESCE-to-current-quarter
  // fallback), so that premature call pulled every historical quarter's row
  // per territory. A second, correct call fires once the quarter loads, but
  // nothing cancels the first - if it resolves later (plausible, since it's
  // the much larger response), it overwrites the correct result. That's the
  // reported "9 UKs, 9 Irelands" bug - one row per historical quarter, not
  // dedupable since the values differ per quarter.
  // `ready` gates the data-fetch effect until both lists have loaded and the
  // default quarter is resolved, matching old's real sequential ordering.
  const [ready, setReady] = useState(false);

  // Matches old's own loadData() Promise.all, which fetches its own
  // timePeriod/territory lists rather than depending on a shared parent
  // list - now genuinely awaited together before anything downstream can
  // fire, same as old.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getTimePeriods().catch(() => []),
      getTerritories().catch(() => []),
    ]).then(([periods, terrs]) => {
      if (cancelled) return;
      setQuarters(periods);
      setTerritories(terrs);
      setQuarter((prev) => prev ?? getDefaultQuarter(periods));
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    onQuarterChange?.(quarter);
  }, [quarter, onQuarterChange]);

  useEffect(() => {
    onTerritoryIdsChange?.(territoryIds);
  }, [territoryIds, onTerritoryIdsChange]);

  const territoryIdsKey = territoryIds.join(",");

  // The one cost-band-detail fetch for this whole page - replaces the three
  // independent ones CostBandTable.jsx/RangeBarsChart.jsx/
  // DistributionChart.jsx used to each make on their own. Gated on `ready`
  // (see note above) so it never fires before the quarter is resolved.
  useEffect(() => {
    if (!jobTitleId || !ready) {
      onDataChange?.({ data: [], loading: !!jobTitleId, error: null });
      return;
    }
    const params = new URLSearchParams({ job_title_id: jobTitleId });
    if (quarter?.id) params.set("time_period_id", quarter.id);
    if (territoryIdsKey) params.set("territory_ids", territoryIdsKey);

    onDataChange?.({ data: [], loading: true, error: null });
    fetch(`/api/cost-band-detail?${params.toString()}`)
      .then((res) => res.json())
      .then((rows) => {
        const sorted = [...rows].sort(
          (a, b) => TERRITORY_SORT_ORDER.indexOf(a.territory_id) - TERRITORY_SORT_ORDER.indexOf(b.territory_id)
        );
        onDataChange?.({ data: sorted, loading: false, error: null });
      })
      .catch((err) => onDataChange?.({ data: [], loading: false, error: err }));
  }, [jobTitleId, ready, quarter, territoryIdsKey, onDataChange]);

  return (
    <div className="fb-row fb-row-static">
      <Dropdown
        label="Quarter"
        value={quarter}
        placeholder="Select quarter"
        options={quarters}
        getLabel={(o) => o.name || o.label}
        onChange={setQuarter}
      />
      <MultiSelectDropdown
        label="Territory"
        values={territoryIds}
        options={territories}
        getLabel={(o) => o.name}
        onChange={setTerritoryIds}
        placeholder="Select territory"
      />
    </div>
  );
}
